import { DeckKey, TarotCardData } from '../types/tarot';
import { supabase, Tables } from '../config/supabase';
import { FarcasterUser } from '../types/farcaster';

export interface SupabaseUserRow {
  id: string;
  fid: number;
  username: string;
  display_name: string;
  avatar_url?: string;
  last_reading_date?: string;
  onboarding_step: number;
  daily_limit: number;
  max_additional_readings: number;
  additional_readings: number;
  seen_onboarding: boolean;
  invited_by_username?: string;
  invited_by_fid?: number;
}

export interface ReadingStats {
  dailyLimit: number;
  dailyUsed: number;
  dailyRemaining: number;
  canDoDaily: boolean;
  maxAdditional: number;
  additionalUsed: number;
  additionalRemaining: number;
  canDoAdditional: boolean;
  totalReadingsToday: number;
}

const databaseDisabled = !supabase || import.meta.env.VITE_DISABLE_SUPABASE === 'true';

const DEFAULT_READING_STATS: ReadingStats = {
  dailyLimit: 1,
  dailyUsed: 0,
  dailyRemaining: 1,
  canDoDaily: true,
  maxAdditional: 2,
  additionalUsed: 0,
  additionalRemaining: 2,
  canDoAdditional: true,
  totalReadingsToday: 0,
};

const DEFAULT_LIMITS_RESPONSE = {
  success: true,
  user: null,
  stats: DEFAULT_READING_STATS,
  canDoDaily: DEFAULT_READING_STATS.canDoDaily,
  canDoAdditional: DEFAULT_READING_STATS.canDoAdditional,
};

// User management with API endpoint - ENHANCED error handling
export const createOrUpdateUser = async (user: FarcasterUser) => {
  if (!supabase || databaseDisabled) {
    console.warn('Database disabled - createOrUpdateUser called without Supabase');
    return null;
  }
  try {
    console.log('🔄 API call: Creating/updating user', { fid: user.fid, username: user.username });
    
    const response = await fetch('/api/user', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fid: user.fid,
        username: user.username,
        display_name: user.display_name,
        avatar_url: user.pfp_url,
      }),
    });

    console.log('📡 API response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorBody = await response.text();
      console.error('❌ API error response:', errorBody);
      throw new Error(`Failed to create/update user: ${response.status} ${response.statusText} - ${errorBody}`);
    }

    const result = await response.json();
    console.log('✅ User API success:', result);
    return result.user;
  } catch (error) {
    console.error('❌ Error creating/updating user:', error);
    console.error('❌ Full error details:', error instanceof Error ? error.stack : error);
    throw error;
  }
};

// Check reading limits (updated for additional readings) - ENHANCED error handling
export const checkReadingLimits = async (fid: number, retryCount = 0) => {
  if (databaseDisabled) {
    console.log('🔌 Database disabled, returning default reading limits');
    return DEFAULT_LIMITS_RESPONSE;
  }
  try {
    // CRITICAL: Validate FID before API call
    if (!fid || isNaN(fid) || fid <= 0) {
      console.error('❌ checkReadingLimits: Invalid FID provided:', fid);
      console.error('❌ Call stack:', new Error().stack);
      throw new Error(`Invalid FID: ${fid}`);
    }
    
    // Simplified logging for production
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 Checking reading limits for FID:', fid, 'attempt:', retryCount + 1);
    }
    
    // Add cache-busting timestamp to prevent stale responses
    const timestamp = Date.now();
    const response = await fetch(`/api/limit?fid=${fid}&_t=${timestamp}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    console.log('📡 Limits API response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorBody = await response.text();
      console.error('❌ Limits API error response:', errorBody);
      
      // Retry once for 5xx errors
      if (response.status >= 500 && retryCount < 1) {
        console.log('🔄 Retrying limits check due to server error...');
        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
        return checkReadingLimits(fid, retryCount + 1);
      }
      
      throw new Error(`Failed to check reading limits: ${response.status} ${response.statusText} - ${errorBody}`);
    }

    const result = await response.json();
    console.log('✅ Limits API success:', result);
    return result;
  } catch (error) {
    console.error('❌ Error checking reading limits:', error);
    console.error('❌ Full error details:', error instanceof Error ? error.stack : error);
    
    // Retry once for network errors
    if (retryCount < 1 && error instanceof Error && error.message.includes('Failed to fetch')) {
      console.log('🔄 Retrying limits check due to network error...');
      await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
      return checkReadingLimits(fid, retryCount + 1);
    }
    
    throw error;
  }
};

// Save reading with API endpoint (updated for reading types)
export const saveReading = async (
  user: FarcasterUser,
  cards: TarotCardData[],
  questionText?: string,
  readingType: 'daily' | 'additional' = 'daily',
  deckKey?: DeckKey,
  retryCount = 0
) => {
  if (databaseDisabled) {
    console.log('🔌 Database disabled, mocking saveReading success');
    return {
      success: true,
      reading: {
        fid: user.fid,
        cards: cards.map(card => card.id),
        selected_cards: cards,
        question_text: questionText || null,
        reading_type: readingType,
        deck_key: deckKey ?? cards[0]?.deckKey ?? 'crypto',
        created_at: new Date().toISOString(),
      },
      readingType,
      stats: {
        ...DEFAULT_READING_STATS,
        dailyUsed: readingType === 'daily' ? 1 : DEFAULT_READING_STATS.dailyUsed,
        additionalUsed: readingType === 'additional' ? 1 : DEFAULT_READING_STATS.additionalUsed,
        totalReadingsToday: DEFAULT_READING_STATS.totalReadingsToday + 1,
        dailyRemaining:
          readingType === 'daily'
            ? Math.max(0, DEFAULT_READING_STATS.dailyLimit - 1)
            : DEFAULT_READING_STATS.dailyRemaining,
        additionalRemaining:
          readingType === 'additional'
            ? Math.max(0, DEFAULT_READING_STATS.maxAdditional - 1)
            : DEFAULT_READING_STATS.additionalRemaining,
      },
    };
  }
  try {
    console.log('💾 Saving reading, attempt:', retryCount + 1, 'Type:', readingType);
    
    // Add cache-busting timestamp
    const timestamp = Date.now();
    const response = await fetch(`/api/save?_t=${timestamp}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fid: user.fid,
        cards: cards.map(card => card.id),
        selectedCards: cards,
        questionText: questionText || null,
        readingType: readingType,
        deckKey: deckKey ?? cards[0]?.deckKey ?? 'crypto',
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('❌ API Error:', errorData);
      
      // Retry once for 5xx errors
      if (response.status >= 500 && retryCount < 1) {
        console.log('🔄 Retrying save due to server error...');
        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
        return saveReading(user, cards, questionText, readingType, deckKey, retryCount + 1);
      }
      
      if (response.status === 429) {
        // Limit reached error - don't retry, this is expected
        const message = errorData.message || `${readingType === 'daily' ? 'Daily' : 'Additional'} limit reached: ${errorData.error}`;
        throw new Error(message);
      }
      
      // For 4xx errors other than 429, retry once
      if (response.status >= 400 && response.status < 500 && retryCount < 1) {
        console.log('🔄 Retrying save due to client error...');
        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
        return saveReading(user, cards, questionText, readingType, deckKey, retryCount + 1);
      }
      
      throw new Error(`Failed to save reading: ${errorData.error || response.statusText}`);
    }

    const result = await response.json();
    console.log('✅ Reading saved successfully:', result);
    return result;
  } catch (error) {
    console.error('❌ Error saving reading:', error);
    
    // Retry once for network errors
    if (retryCount < 1 && error instanceof Error && error.message.includes('Failed to fetch')) {
      console.log('🔄 Retrying save due to network error...');
      await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
      return saveReading(user, cards, questionText, readingType, deckKey, retryCount + 1);
    }
    
    throw error;
  }
};

// Reset user readings for testing - ENHANCED error handling
export const resetUserReadings = async (fid: number) => {
  if (databaseDisabled) {
    console.log('🔌 Database disabled, skipping resetUserReadings');
    return { success: true };
  }
  try {
    console.log('🔄 API call: Resetting readings for FID:', fid);
    
    const response = await fetch('/api/reset', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fid: fid,
      }),
    });

    console.log('📡 Reset API response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorBody = await response.text();
      console.error('❌ Reset API error response:', errorBody);
      throw new Error(`Failed to reset readings: ${response.status} ${response.statusText} - ${errorBody}`);
    }

    const result = await response.json();
    console.log('✅ Reset API success:', result);
    return result;
  } catch (error) {
    console.error('❌ Error resetting readings:', error);
    console.error('❌ Full error details:', error instanceof Error ? error.stack : error);
    throw error;
  }
};

// Update user onboarding step
export const updateOnboardingStep = async (fid: number, onboardingStep: number) => {
  if (databaseDisabled) {
    console.log('🔌 Database disabled, skipping updateOnboardingStep');
    return null;
  }
  try {
    const response = await fetch('/api/onboarding', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fid: fid,
        onboarding_step: onboardingStep,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update onboarding step: ${response.statusText}`);
    }

    const result = await response.json();
    return result.user;
  } catch (error) {
    console.error('Error updating onboarding step:', error);
    throw error;
  }
};

// Get reading history - DEPRECATED: Use getReadingHistoryByFid instead
export const getReadingHistory = async (userId: string, limit = 10) => {
  console.warn('⚠️ getReadingHistory is deprecated - use getReadingHistoryByFid instead');
  // This function is kept for backward compatibility but should not be used
  // Convert userId to FID if needed or throw error
  throw new Error('getReadingHistory is deprecated - use getReadingHistoryByFid with FID instead');
};

// Get reading history by FID (more reliable for device sync) - ENHANCED with API fallback
export const getReadingHistoryByFid = async (fid: number, limit = 10) => {
  if (!fid || isNaN(fid)) {
    throw new Error(`Invalid FID provided for reading history: ${fid}`);
  }

  if (databaseDisabled) {
    console.log('🔌 Database disabled, returning empty reading history');
    return [];
  }

  return await fetchReadingHistoryViaApi(fid, limit);
};

const fetchReadingHistoryViaApi = async (fid: number, limit = 10) => {
  const endpoint = `/api/readings?fid=${encodeURIComponent(fid)}&limit=${encodeURIComponent(limit)}`;
  console.log('🌐 API FALLBACK: Fetching reading history via', endpoint);

  try {
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ API FALLBACK: Failed to fetch readings:', response.status, response.statusText, errorText);
      throw new Error(`Readings API error: ${response.status}`);
    }

    const payload = await response.json();
    const readings = payload?.readings ?? [];
    console.log('✅ API FALLBACK: Received', readings.length, 'readings from server');
    return readings;
  } catch (error) {
    console.error('❌ API FALLBACK: Unexpected error while fetching readings:', error);
    throw error;
  }
};

// Mark reading as shared
export const markReadingAsShared = async (readingId: string, shareUrl: string) => {
  if (databaseDisabled) {
    console.log('🔌 Database disabled, skipping markReadingAsShared');
    return null;
  }
  
  const { data, error } = await supabase
    .from(Tables.READINGS)
    .update({
      shared: true,
      share_url: shareUrl,
    })
    .eq('id', readingId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

// Legacy function for backward compatibility
export const checkDailyLimit = async (fid: number) => {
  return await checkReadingLimits(fid);
};

// Legacy function for backward compatibility
export const upsertUser = async (
  dbUser: SupabaseUserRow,
  user: FarcasterUser
) => {
  return await createOrUpdateUser(user);
};

// Upsert or update streak (via API to avoid direct Supabase dependency)
export const updateUserStreak = async (fid: number) => {
  if (databaseDisabled) {
    console.log('🔌 Database disabled, skipping updateUserStreak');
    return null;
  }
  try {
    // Simplified logging for production
    if (process.env.NODE_ENV === 'development') {
      console.log('🔥 Updating user streak for FID:', fid);
    }
    
    const response = await fetch('/api/streak', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fid: fid,
      }),
    });

    if (!response.ok) {
      console.warn('⚠️ Streak API not available, continuing without streak update');
      return null; // Don't throw error - just skip streak update
    }

    const result = await response.json();
    console.log('✅ User streak updated via API:', result);
    return result;
  } catch (error) {
    console.warn('⚠️ Failed to update streak, continuing without it:', error);
    return null; // Don't throw error - streak is not critical
  }
};

export const getUserStreak = async (userId: string) => {
  if (databaseDisabled) {
    console.log('🔌 Database disabled, returning null streak');
    return null;
  }
  try {
    // CRITICAL: Validate FID before API call to prevent NaN errors
    const fid = parseInt(userId);
    if (!userId || isNaN(fid) || fid <= 0) {
      console.error('❌ getUserStreak: Invalid FID provided:', userId, 'parsed:', fid);
      console.error('❌ Call stack:', new Error().stack);
      return null;
    }
    
    // Simplified logging for production
    if (process.env.NODE_ENV === 'development') {
      console.log('🔥 Getting user streak for FID:', fid);
    }
    
    const response = await fetch(`/api/streak?fid=${fid}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      if (response.status === 404) {
        console.log('🔥 No streak found for user, returning null');
        return null;
      }
      throw new Error(`Streak API error: ${response.status}`);
    }

    const result = await response.json();
    console.log('✅ Streak fetched via API:', result.streak);
    return result.streak;
  } catch (error) {
    console.warn('⚠️ Streak API not available, fallback to direct query:', error);
    
    // Fallback to direct Supabase query using FID
    console.log('🔥 Fallback: Using direct Supabase query with FID:', userId);
    const { data, error: supabaseError } = await supabase
      .from(Tables.STREAKS)
      .select('*')
      .eq('fid', parseInt(userId.toString()))
      .single();

    if (supabaseError && supabaseError.code !== 'PGRST116') { // PGRST116 is "not found"
      throw supabaseError;
    }

    return data;
  }
};

// Analytics helpers
export const getTopReadings = async (limit = 10) => {
  if (databaseDisabled) {
    console.log('🔌 Database disabled, returning empty top readings');
    return [];
  }
  
  const { data, error } = await supabase
    .from(Tables.READINGS)
    .select('*')
    .eq('shared', true)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return data;
};

export const getUserStats = async (fid: number) => {
  if (databaseDisabled) {
    console.log('🔌 Database disabled, returning mock user stats');
    return {
      totalReadings: 0,
      sharedReadings: 0,
      currentStreak: 0,
      lastReadingDate: null,
    };
  }
  console.log('📊 Getting user stats for FID:', fid);
  const { data: readings, error: readingsError } = await supabase
    .from(Tables.READINGS)
    .select('*')
    .eq('fid', fid);

  if (readingsError) throw readingsError;

  const { data: streak, error: streakError } = await supabase
    .from(Tables.STREAKS)
    .select('*')
    .eq('fid', fid)
    .single();

  if (streakError && streakError.code !== 'PGRST116') throw streakError;

  return {
    totalReadings: readings?.length || 0,
    sharedReadings: readings?.filter(r => r.shared).length || 0,
    currentStreak: streak?.count || 0,
    lastReadingDate: streak?.last_reading_date || null,
  };
}; 

// Update last_reading_date in users table - FID-first approach
export const updateLastReadingDate = async (
  fid: number
) => {
  if (databaseDisabled) {
    console.log('🔌 Database disabled, skipping updateLastReadingDate');
    return null;
  }
  console.log('📅 Updating last reading date for FID:', fid);
  const { data, error } = await supabase
    .from(Tables.USERS)
    .update({ 
      last_reading_date: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })
    .eq('fid', fid)
    .select()
    .single();

  if (error) throw error;
  return data;
}; 
